import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Push Notification utility for Capacitor apps.
 * In the web browser, this is a no-op (Capacitor native handles the actual push).
 * The web code registers the token with the backend when available.
 */

let currentToken = null;

// Check if running inside Capacitor native shell
const isCapacitor = () => {
  return window.Capacitor !== undefined;
};

/**
 * Initialize push notifications after login.
 * Call this after the user successfully logs in.
 */
export const initPushNotifications = async () => {
  if (!isCapacitor()) {
    console.log('Push notifications: not running in Capacitor, skipping native registration');
    return;
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Push notification permission denied');
      return;
    }

    // Register with APNs / FCM
    await PushNotifications.register();

    // Listen for registration success
    PushNotifications.addListener('registration', async (token) => {
      console.log('FCM Token received:', token.value);
      currentToken = token.value;
      await saveTokenToBackend(token.value);
    });

    // Listen for registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // Listen for incoming notifications (foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      // You can show an in-app toast/banner here
    });

    // Listen for notification tap (background/closed)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action:', action);
      const data = action.notification.data;
      if (data?.type === 'new_launch' && data?.launch_id) {
        // Navigate to financial launches page
        window.location.href = '/admin/financial-launches';
      }
    });

  } catch (error) {
    console.log('Push notifications not available:', error.message);
  }
};

/**
 * Save FCM token to backend
 */
const saveTokenToBackend = async (token) => {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;
    
    await axios.post(`${API_URL}/auth/fcm-token`, 
      { token },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    console.log('FCM token saved to backend');
  } catch (error) {
    console.error('Failed to save FCM token:', error);
  }
};

/**
 * Remove FCM token from backend (call on logout)
 */
export const removePushToken = async () => {
  if (!currentToken) return;
  
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    await axios.delete(`${API_URL}/auth/fcm-token`, {
      data: { token: currentToken },
      headers: { Authorization: `Bearer ${authToken}` }
    });
    currentToken = null;
    console.log('FCM token removed from backend');
  } catch (error) {
    console.error('Failed to remove FCM token:', error);
  }
};
